/**
 * Durable note recordings: every chunk of a desktop "Voice to Text" take is
 * written to IndexedDB as it arrives, and the take is removed only once the
 * upload has been FINALIZED. Until then a closed laptop lid, a discarded tab, a
 * reload, a crash or a sign-out leaves the audio on disk for
 * `RecordingRecoveryPrompt` to offer back on the next authenticated page load.
 *
 * Why: on 2026-10-18 a ~1 h client meeting recorded on desktop Chrome never
 * reached `POST /api/uploads/reserve`. The take lived only in the JS heap
 * (`audioChunksRef`) and React state, so closing the lid while it said
 * "Uploading" lost all of it, with nothing on the server to recover.
 *
 * Same storage pattern as mobile Quick Capture (`usePendingCapture.ts`): raw
 * IndexedDB, Blobs stored directly by structured clone (Chrome keeps the bytes
 * on disk, not in the heap), every call best-effort. A separate database
 * because that one holds ONE capture under a fixed key, and this needs many
 * takes, appended to chunk by chunk.
 *
 * PHI at rest: the audio is on the device's disk, readable by this origin in
 * this browser profile, until it is finalized, discarded, or `MAX_AGE_MS`
 * passes. Sign-out deliberately does NOT clear it (losing the take is the
 * incident this exists for); instead every take carries its owner's user id
 * and is only ever offered back to that user (`listRecoverableRecordings`).
 *
 * Ownership across tabs: the tab recording or uploading a take holds a Web Lock
 * on it (`holdRecording`). A take nobody holds is orphaned and recoverable; a
 * held one belongs to a live recorder in this tab or another and is never
 * offered, so two tabs cannot upload the same take.
 */

export interface DurableRecordingMeta {
  id: string;
  /** The signed-in user's internal id (Firebase uid only if that is all we had). */
  ownerId: string;
  workspaceId: string;
  artifactId: string;
  mimeType: string;
  startedAt: number;
  updatedAt: number;
  durationMs: number;
  multipleSpeakers: boolean;
  /** 'recording' while chunks are still arriving; 'stopped' once the take is complete. */
  status: 'recording' | 'stopped';
  chunkCount: number;
  /** Finalize-only resume handle from an ambiguous finalize, so recovery cannot duplicate. */
  resumeFinalizeUploadId?: string;
  /**
   * The server has it, but deleting this copy failed. Never offered again (that
   * would upload a duplicate); the next sweep retries the delete.
   */
  finalized?: boolean;
  /** A write failed mid-take: only the start of it is on disk. */
  partial?: boolean;
}

const DB_NAME = 'bower-recordings';
const DB_VERSION = 1;
const META = 'meta';
const CHUNKS = 'chunks';
/**
 * A week from when the take STARTED: long enough to come back after a weekend,
 * short enough to bound PHI at rest. Measured from startedAt, not the last
 * write, so failed retries cannot keep extending it.
 */
export const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const LOCK_PREFIX = 'bower-recording:';

const chunkKey = (id: string, seq: number) => `${id}#${seq}`;

// One connection per IndexedDB factory, reused by every write: a take writes
// about once a second, and a fresh `open()` per write leaves thousands of
// connections per hour of recording. Keyed by the factory so a swapped one
// (tests) gets its own; a failed open or a closed connection is retried.
let connection: { factory: IDBFactory; db: Promise<IDBDatabase> } | null = null;

function openDB(): Promise<IDBDatabase> {
  if (connection?.factory === indexedDB) return connection.db;
  const db = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const opened = req.result;
      if (!opened.objectStoreNames.contains(META)) opened.createObjectStore(META);
      if (!opened.objectStoreNames.contains(CHUNKS)) opened.createObjectStore(CHUNKS);
    };
    req.onsuccess = () => {
      const opened = req.result;
      // Another tab upgrading the schema, or the browser closing it: let go,
      // and open afresh next time.
      opened.onversionchange = () => {
        opened.close();
        connection = null;
      };
      opened.onclose = () => {
        connection = null;
      };
      resolve(opened);
    };
    req.onerror = () => reject(req.error);
    // An older connection elsewhere is not letting go: fail rather than wait.
    req.onblocked = () => reject(new DOMException('IndexedDB open blocked', 'AbortError'));
  });
  connection = { factory: indexedDB, db };
  db.catch(() => {
    if (connection?.db === db) connection = null;
  });
  return db;
}

function done<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function committed(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
  });
}

function isMeta(val: unknown): val is DurableRecordingMeta {
  if (!val || typeof val !== 'object') return false;
  const m = val as Record<string, unknown>;
  return (
    typeof m.id === 'string' &&
    typeof m.ownerId === 'string' &&
    typeof m.workspaceId === 'string' &&
    typeof m.artifactId === 'string' &&
    typeof m.mimeType === 'string' &&
    typeof m.startedAt === 'number' &&
    typeof m.updatedAt === 'number' &&
    typeof m.chunkCount === 'number'
  );
}

/** Ask the browser not to evict this origin's storage under pressure. Advisory. */
export async function requestPersistentStorage(): Promise<void> {
  try {
    await navigator.storage?.persist?.();
  } catch {
    // Best-effort, as in usePendingCapture.
  }
}

/**
 * Writes one take, in order. The recorder hands every chunk to `append`; they
 * are batched in memory and flushed as ONE Blob per transaction so an hour at a
 * 100 ms timeslice is a few thousand writes, not 36 000. A flush also happens
 * on pause, on stop, and when the tab is hidden (the lid-close path), so what
 * is lost on a hard kill is at most the last batch.
 *
 * Unlike usePendingCapture's silent best-effort, a failed write is REPORTED
 * once through `onPersistError` (rule 12): the take still uploads from memory,
 * but the user has to know it is not backed up.
 */
export class DurableRecordingWriter {
  private batch: Blob[] = [];
  private chain: Promise<void> = Promise.resolve();
  private seq = 0;
  private failed = false;
  private closed = false;
  readonly meta: DurableRecordingMeta;

  constructor(
    meta: Omit<DurableRecordingMeta, 'updatedAt' | 'chunkCount' | 'status' | 'durationMs'>,
    private readonly onPersistError: (error: unknown) => void,
    private readonly batchSize = 10,
  ) {
    this.meta = {
      ...meta,
      updatedAt: meta.startedAt,
      chunkCount: 0,
      status: 'recording',
      durationMs: 0,
    };
    this.enqueue(async () => {
      const db = await openDB();
      const tx = db.transaction(META, 'readwrite');
      tx.objectStore(META).put({ ...this.meta }, this.meta.id);
      await committed(tx);
    });
  }

  append(chunk: Blob, durationMs: number): void {
    if (this.closed) return;
    this.batch.push(chunk);
    this.meta.durationMs = durationMs;
    if (this.batch.length >= this.batchSize) this.flush();
  }

  /** Write whatever is batched. Resolves once everything queued so far is on disk. */
  flush(): Promise<void> {
    if (!this.closed && this.batch.length > 0) {
      const blob = new Blob(this.batch, { type: this.meta.mimeType });
      // The duration this batch reaches, captured now: the write may run after
      // finish() has set the whole take's, and if a later write then fails,
      // this row is what the partial take is offered with.
      const { durationMs } = this.meta;
      this.batch = [];
      const seq = this.seq++;
      this.enqueue(async () => {
        const db = await openDB();
        const tx = db.transaction([META, CHUNKS], 'readwrite');
        const metaStore = tx.objectStore(META);
        // Merge, never overwrite: a resume handle written beside this queue
        // (after an ambiguous finalize) belongs to the row, and a late tail
        // flush must not erase it.
        const onDisk: unknown = await done(metaStore.get(this.meta.id));
        tx.objectStore(CHUNKS).put(blob, chunkKey(this.meta.id, seq));
        const next = {
          ...this.meta,
          ...(isMeta(onDisk) && { resumeFinalizeUploadId: onDisk.resumeFinalizeUploadId }),
          durationMs,
          chunkCount: seq + 1,
          updatedAt: Date.now(),
        };
        metaStore.put(next, this.meta.id);
        await committed(tx);
        this.meta.chunkCount = next.chunkCount;
        this.meta.updatedAt = next.updatedAt;
      });
    }
    return this.chain;
  }

  /** Flush the tail and mark the take complete, with the speaker mode as it ended. */
  finish(durationMs: number, multipleSpeakers: boolean): Promise<void> {
    this.meta.durationMs = durationMs;
    this.meta.multipleSpeakers = multipleSpeakers;
    void this.flush();
    // After a failed write the disk holds only a prefix: keep the duration of
    // what is there, and say so, rather than offer it as the whole take.
    this.enqueue(
      () =>
        updateMeta(
          this.meta.id,
          this.failed
            ? { status: 'stopped', multipleSpeakers, partial: true }
            : { status: 'stopped', durationMs, multipleSpeakers },
        ),
      true,
    );
    return this.chain;
  }

  /**
   * Delete the take, AFTER every write already queued. A delete issued beside
   * the queue could land before a pending flush, which would then re-create the
   * take (offered back after the user discarded it) or strand a chunk with no
   * meta row (never expired). Resolves false when the delete did not land.
   */
  discard(): Promise<boolean> {
    this.closed = true;
    this.batch = [];
    writesInFlight.delete(this.meta.id);
    const deleted = this.chain
      .then(() => deleteRecording(this.meta.id))
      .then(
        () => true,
        () => false,
      );
    this.chain = deleted.then(() => undefined);
    return deleted;
  }

  /**
   * After the first failed write, chunks stop: the copy on disk stays an
   * in-order, uploadable prefix (with a gap it could never be reassembled).
   * Only the closing status update still runs (`always`).
   */
  private enqueue(write: () => Promise<void>, always = false): void {
    this.chain = this.chain
      .then(() => (this.failed && !always ? undefined : write()))
      .catch((error) => {
        if (this.failed) return;
        this.failed = true;
        this.onPersistError(error);
      });
    writesInFlight.set(this.meta.id, this.chain);
  }
}

export async function updateMeta(id: string, patch: Partial<DurableRecordingMeta>): Promise<void> {
  const db = await openDB();
  const tx = db.transaction(META, 'readwrite');
  const store = tx.objectStore(META);
  const current = await done(store.get(id));
  if (isMeta(current)) store.put({ ...current, ...patch, updatedAt: Date.now() }, id);
  await committed(tx);
}

// ── Finalized, before the disk agrees ──────────────────────────────────────
// The server has the take the moment finalize succeeds, but the on-disk copy
// can only be cleared after its own queued writes, which a stalled IndexedDB
// can hold up. A tab closed in that window would leave a take that is offered
// again (a duplicate upload). localStorage is synchronous, so the id is
// recorded before anything can stall; it holds ids only, never content.
const FINALIZED_KEY = 'bower:finalized-recordings';

function finalizedIds(): string[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(FINALIZED_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

function setFinalizedIds(ids: string[]): void {
  try {
    if (ids.length === 0) localStorage.removeItem(FINALIZED_KEY);
    else localStorage.setItem(FINALIZED_KEY, JSON.stringify(ids.slice(-50)));
  } catch {
    // Storage blocked: the IndexedDB `finalized` mark is the fallback.
  }
}

/** Each live writer's queue, so a clear can land after its last write. */
const writesInFlight = new Map<string, Promise<void>>();

/**
 * Remove a take the server now has. When the delete fails, mark it finalized so
 * it is never offered back for a duplicate upload; the next sweep retries the
 * delete. Resolves false when the copy is still on disk.
 */
export async function clearFinalizedRecording(id: string): Promise<boolean> {
  // Synchronously, before any await can stall (see FINALIZED_KEY).
  setFinalizedIds([...finalizedIds().filter((v) => v !== id), id]);
  // A tail still being written (finish() is not awaited past a timeout before
  // the upload) would otherwise re-create the take after this delete.
  await writesInFlight.get(id);
  writesInFlight.delete(id);
  try {
    await deleteRecording(id);
    setFinalizedIds(finalizedIds().filter((v) => v !== id));
    return true;
  } catch {
    await updateMeta(id, { finalized: true }).catch(() => undefined);
    return false;
  }
}

/** Every stored take's metadata, with no ownership or lock filtering. */
async function listAll(): Promise<DurableRecordingMeta[]> {
  const db = await openDB();
  const all = await done(db.transaction(META, 'readonly').objectStore(META).getAll());
  return all.filter(isMeta);
}

/** Whether the take is still on this device (another tab may have cleared it). */
export async function recordingExists(id: string): Promise<boolean> {
  const db = await openDB();
  return isMeta(await done(db.transaction(META, 'readonly').objectStore(META).get(id)));
}

/** The take's audio, reassembled in order. Null if any chunk is missing. */
export async function readRecordingBlob(meta: DurableRecordingMeta): Promise<Blob | null> {
  const db = await openDB();
  const store = db.transaction(CHUNKS, 'readonly').objectStore(CHUNKS);
  const parts = await Promise.all(
    Array.from({ length: meta.chunkCount }, (_, seq) =>
      done(store.get(chunkKey(meta.id, seq)) as IDBRequest<unknown>),
    ),
  );
  if (parts.some((p) => !(p instanceof Blob))) return null;
  return new Blob(parts as Blob[], { type: meta.mimeType });
}

/**
 * Remove a take. Called after finalize succeeds, after an explicit discard, and
 * for takes too short to upload. Throws so the caller can tell a delete that
 * did not land (rule 12); a take left behind would be offered again.
 */
export async function deleteRecording(id: string): Promise<void> {
  const db = await openDB();
  const meta = await done(db.transaction(META, 'readonly').objectStore(META).get(id));
  // A batch's chunk and its chunkCount bump commit in one transaction, so
  // chunkCount is exactly the chunks on disk.
  const chunkCount = isMeta(meta) ? meta.chunkCount : 0;
  const tx = db.transaction([META, CHUNKS], 'readwrite');
  for (let seq = 0; seq < chunkCount; seq++) tx.objectStore(CHUNKS).delete(chunkKey(id, seq));
  tx.objectStore(META).delete(id);
  await committed(tx);
}

// ── Ownership (who is recording or uploading a take right now) ───────────────

/** Takes held by this tab. Covers browsers without Web Locks, and same-tab checks. */
const heldHere = new Set<string>();
const releaseListeners = new Set<() => void>();

/** Run `listener` whenever a take is released, so the recovery prompt can re-scan. */
export function onRecordingReleased(listener: () => void): () => void {
  releaseListeners.add(listener);
  return () => releaseListeners.delete(listener);
}

/**
 * Claim a take for this tab until the returned release is called. Uses a Web
 * Lock so other tabs see it as live. A recorder claims a take it just created
 * and waits for the grant; the recovery prompt passes `ifAvailable` and gets
 * null when another tab got there first, so two tabs never upload one take.
 */
export async function holdRecording(
  id: string,
  { ifAvailable = false }: { ifAvailable?: boolean } = {},
): Promise<(() => void) | null> {
  if (heldHere.has(id)) return ifAvailable ? null : () => undefined;
  heldHere.add(id);
  let releaseLock: (() => void) | undefined;
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (locks) {
    const got = await new Promise<boolean>((granted) => {
      void locks
        .request(LOCK_PREFIX + id, { ifAvailable }, (lock) => {
          if (!lock) {
            granted(false);
            return undefined;
          }
          granted(true);
          return new Promise<void>((resolve) => {
            releaseLock = resolve;
          });
        })
        // No usable Web Locks (an opaque origin): this tab's own claim stands.
        .catch(() => granted(true));
    });
    if (!got) {
      heldHere.delete(id);
      return null;
    }
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    heldHere.delete(id);
    releaseLock?.();
    releaseListeners.forEach((listener) => listener());
  };
}

async function heldElsewhere(): Promise<Set<string>> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks?.query) return new Set();
  try {
    const { held = [] } = await locks.query();
    return new Set(
      held
        .map((lock) => lock.name ?? '')
        .filter((name) => name.startsWith(LOCK_PREFIX))
        .map((name) => name.slice(LOCK_PREFIX.length)),
    );
  } catch {
    return new Set();
  }
}

/**
 * Delete every take started `MAX_AGE_MS` or more ago, whoever owns it, and
 * every take already on the server. Runs on every app load with or without a signed-in
 * user (`App`), so a shared browser nobody signs in to again still loses the
 * audio. Returns the takes left.
 */
export async function sweepRecordings(now = Date.now()): Promise<DurableRecordingMeta[]> {
  const all = await listAll();
  const finalized = new Set(finalizedIds());
  // Never a take a tab still holds: its owner may be finishing its writes
  // before its own clear, and deleting under it would let a queued write
  // re-create the row with its first chunks gone.
  const heldByAnyTab = new Set([...heldHere, ...(await heldElsewhere())]);
  const gone = all.filter(
    (m) =>
      !heldByAnyTab.has(m.id) &&
      (m.finalized || finalized.has(m.id) || now - m.startedAt >= MAX_AGE_MS),
  );
  const deleted = await Promise.all(
    gone.map((m) =>
      deleteRecording(m.id).then(
        () => m.id,
        () => null,
      ),
    ),
  );
  // Forget only ids confirmed gone (deleted now, or not on disk at all), and
  // re-read the list first: another tab may have added one meanwhile.
  const onDisk = new Set(all.map((m) => m.id));
  const confirmedGone = new Set([
    ...deleted.filter((id): id is string => id !== null),
    ...[...finalized].filter((id) => !onDisk.has(id)),
  ]);
  setFinalizedIds(finalizedIds().filter((id) => !confirmedGone.has(id)));
  return all.filter((m) => !gone.includes(m));
}

/**
 * The takes this user can recover: theirs, not live in any tab, not expired,
 * not already on the server. Another user's takes are never returned.
 */
export async function listRecoverableRecordings(
  ownerIds: readonly string[],
  now = Date.now(),
): Promise<DurableRecordingMeta[]> {
  const left = await sweepRecordings(now);
  const held = await heldElsewhere();
  return left
    .filter((m) => ownerIds.includes(m.ownerId))
    .filter((m) => !heldHere.has(m.id) && !held.has(m.id))
    .sort((a, b) => a.startedAt - b.startedAt);
}
